import FormattedMessage from 'I18n/components/FormattedMessage';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { hasPricingAndPurchasingSucceededSelector, isBusinessObjectivesLoadingSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import { BUNDLE_CARD } from '../../constants/tracking/Locations';
import { useIsMobile } from '../../context/IsMobileContext';
import { useAppSelector } from '../../reducers';
import { calculatedPriceSelector } from '../../selectors/PricingSelectors';
import { bundledProductOwnershipLevelSelector, hasProductVersionInProductInventorySelector, quantityPackProductConfigurationsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { bundleProductForBundleTypeAndProductLevelSelector } from '../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import DetailedTermSelector from '../common/DetailedTermSelector';
import { SectionTitle } from '../common/SharedComponents';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { HighlightedPriceDisplay } from '../common/pricing/HighlightedPriceDisplay';
import AdditionalUnitsPicker from '../plan-builder/AdditionalUnitsPicker';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import GenericBundleCTAsContainer from './GenericBundleCTAsContainer';
import IncludedSeatsDisplay from './IncludedSeatsDisplay';
import { MerchCardWrapper, Section, borderStyle } from './MerchCardSharedComponents';
import BundleBusinessObjectives from './business-objectives/BundleBusinessObjectives';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BundleTile = ({
  productLevel,
  bundleType,
  screen,
  commonStyles,
  renderPrimaryBadge,
  renderSecondaryBadge
}) => {
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const isBusinessObjectivesLoading = useAppSelector(isBusinessObjectivesLoadingSelector);
  const product = useAppSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return;
    return bundleProductForBundleTypeAndProductLevelSelector(state, {
      bundleType,
      productLevel
    });
  });
  const {
    currentSkuId: skuId
  } = product || {};
  const ownsProductVersion = useAppSelector(state => skuId && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const isCurrentPlanLevel = useAppSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return false;
    return bundledProductOwnershipLevelSelector(state, {
      bundleType
    }) === productLevel;
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const ownedQuantityPackProductConfigurations = useAppSelector(state => {
    if (!skuId) return null;
    return quantityPackProductConfigurationsFromProductInventorySelector(state, {
      baseProductConfigurations: [createProductConfig({
        skuId
      })]
    });
  }) || [];
  const pricingInfo = useAppSelector(state => {
    if (!skuId) {
      return null;
    }
    const checkoutProductConfigurations = [createProductConfig({
      skuId
    }), ...ownedQuantityPackProductConfigurations];
    return calculatedPriceSelector(state, {
      productConfigurations: checkoutProductConfigurations,
      queryParamTerm
    });
  });
  const shouldShowTermSelector = useAppSelector(state => skuId && shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }));
  const isMobile = useIsMobile();
  const {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity
  } = useSkuAdditionalUnits({
    bundleType,
    productLevel
  });
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    compact: true,
    queryParamTerm,
    trackingSource: BUNDLE_CARD,
    productLevel
  };
  if (!hasPricingAndPurchasingSucceeded || !product) return null;
  const {
    apiName
  } = product;
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${apiName}-merch-card`,
      className: "flex-column p-top-8",
      style: Object.assign({
        borderTop: isMobile ? borderStyle : undefined
      }, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel ? /*#__PURE__*/_jsx(CurrentPlanTag, {}) : null, renderPrimaryBadge ? renderPrimaryBadge() : null, renderSecondaryBadge ? renderSecondaryBadge() : null, /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `bundleTile.${bundleType}.${productLevel}.title`
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        zIndex: 1
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `bundleTile.${bundleType}.${productLevel}.description`
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: HighlightedPriceDisplay,
        contentProps: {
          pricingInfo,
          skuId,
          queryParamTerm,
          showDiscount: true,
          useLargePrice: true
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: IncludedSeatsDisplay,
        contentProps: {
          skuId,
          queryParamTerm
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: shouldShowTermSelector ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [41],
        widths: [300],
        className: "flex-column align-center",
        Content: DetailedTermSelector,
        contentProps: {
          skuIds: [skuId],
          onChange: term => setQueryParamTerm(term),
          activeTerm: queryParamTerm,
          trackingSource: BUNDLE_CARD
        }
      }) : null
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      }),
      children: !ownsProductVersion && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-start",
        heights: [34, 76],
        widths: ['75%', '100%'],
        Content: AdditionalUnitsPicker,
        contentProps: additionalUnitsPickerProps
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        widths: [300],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(GenericBundleCTAsContainer, {
          apiName: apiName,
          screen: screen,
          trackingSource: BUNDLE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 8
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex-column",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 9
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isBusinessObjectivesLoading,
        heights: [48, 48],
        widths: [300, 300],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(BundleBusinessObjectives, {
          bundleType: bundleType,
          productLevel: productLevel,
          apiName: apiName,
          collapseBusinessObjectives: isMobile
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 p-top-3",
      style: Object.assign({
        borderBottom: isMobile ? borderStyle : undefined
      }, commonStyles, {
        gridRowStart: 11
      }),
      children: skuId && /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
        queryParamTerm: queryParamTerm,
        skuIds: [skuId],
        trackingSource: BUNDLE_CARD,
        includesOnboardingCost: false
      })
    })]
  });
};
export default BundleTile;